import React, { ReactElement, useMemo, useState } from 'react';
import { Box } from 'rebass/styled-components';

import { ReactSelect } from 'ui/patterns/reactSelect';
import { SlidePanel, FormInput, Loader } from 'ui/patterns';
import { Button, Stack } from 'ui/components';
import { SegmentedButtons } from 'ui/patterns';

import { useGetExistingTemplates } from 'hooks';

import { confirmAlert } from 'utils/confirm-alert';
import { showErrorToast } from 'utils/showToast';

import { BlobType } from 'types/procedureBuilder';
import { SaveLayoutProps } from './saveLayoutTypes';
import { checkAzureFileNameValidation } from 'utils/fileUtils';

function SaveLayout({
  showPanel,
  togglePanel,
  onSavePress,
  accountId,
}: SaveLayoutProps): ReactElement {
  const [title, setTitle] = useState<string>();
  const [saveOption, setSaveOption] = useState(true);

  const { data: layoutData, isLoading: layoutDataLoading } =
    useGetExistingTemplates({ showPanel, accountId, templateType: 'layouts' });

  const exisitingLayoutOptions = useMemo(() => {
    return (
      layoutData &&
      layoutData.Data &&
      layoutData.Data[0].Blobs.filter(
        (data: BlobType) => data?.Type === 'html',
      ).map((item: BlobType, index: number) => {
        return {
          value: index,
          label: item.Name,
        };
      })
    );
  }, [layoutData]);

  const selectExisitingLayout = (value: number) => {
    const selected = exisitingLayoutOptions?.filter(
      (item: { label: string; value: number }) => {
        if (item.value === value) return item;
      },
    );
    if (selected) {
      setTitle(selected[0].label);
    }
  };

  const existingLayoutCheck = (title: string | undefined) => {
    const check = title?.toLowerCase().includes('.html');
    let value: string | undefined;
    if (check) value = title?.split('.')[0]?.trim();
    else value = title?.trim();
    const checkedValue = exisitingLayoutOptions?.filter((item: any) => {
      if (item.label.split('.')[0] === value) return item;
    });
    if (checkedValue?.length) return true;
    else return false;
  };

  const saveLayoutActions = () => (
    <>
      <Button variant="secondary" onClick={() => togglePanel()}>
        Cancel
      </Button>
      <Button
        onClick={() => {
          const validationMessage = checkAzureFileNameValidation(title || '');
          if (!title || !title.trim() || validationMessage !== 'Valid') {
            showErrorToast({
              message: validationMessage,
            });
            return;
          } else if (existingLayoutCheck(title)) {
            if (saveOption) {
              confirmAlert({
                message:
                  'Layout with the same name exists. Do you want to overwrite the current layout?',
                buttons: [
                  {
                    label: 'Yes',
                    onClick: () => {
                      const check = title?.toLowerCase().endsWith('.html');
                      let value: string | undefined;
                      if (!check) value = `${title?.trim()}.html`;
                      //else value = `${title?.split('.')[0]?.trim()}.html`;
                      onSavePress(value || '', false);
                    },
                    className: 'pr-btn-primary',
                  },
                  {
                    label: 'No',
                    onClick: () => {
                      //
                    },
                  },
                ],
              });
            } else {
              const check = title?.toLowerCase().endsWith('.html');
              let value: string | undefined;
              if (!check) value = `${title?.trim()}.html`;
              //else value = `${title?.split('.')[0]?.trim()}.html`;
              onSavePress(value || '', false);
            }
          } else {
            const check = title?.toLowerCase().endsWith('.html');
            let value: string = title?.trim() || '';
            if (!check) value = `${value}.html`;
            onSavePress(value || '', saveOption);
          }
        }}
        variant="primary"
      >
        Save Layout
      </Button>
    </>
  );

  return (
    <SlidePanel
      open={showPanel}
      onClose={() => {
        togglePanel();
      }}
      size="sm"
      title="Save Layout"
      actions={saveLayoutActions()}
    >
      <Stack direction="y" gap={5} padding={3}>
        <SegmentedButtons width="100%" equalWidthChildren>
          <Button
            variant={saveOption ? 'primary' : 'outline'}
            onClick={() => setSaveOption(true)}
          >
            Save
          </Button>
          <Button
            variant={!saveOption ? 'primary' : 'outline'}
            onClick={() => setSaveOption(false)}
          >
            Save as
          </Button>
        </SegmentedButtons>
        {saveOption ? (
          <FormInput onTextChange={setTitle} label="Enter layout name" />
        ) : null}
        {!saveOption ? (
          <Box>
            <ReactSelect
              name="layoutOptions"
              label="Select Layout"
              isSearchable={true}
              isClearable={true}
              options={exisitingLayoutOptions ? exisitingLayoutOptions : []}
              onChange={(e: any) => {
                if (e) selectExisitingLayout(e.value);
              }}
              classNamePrefix="pr-select"
            />
          </Box>
        ) : null}
      </Stack>
      {layoutDataLoading && !saveOption && <Loader />}
    </SlidePanel>
  );
}

export default SaveLayout;
